import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Play, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import type { FinishedMatch, MatchTimeline } from "@/types";
import { scorePoints, type ScoredPoint } from "@/lib/matchScoring";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { MatchVideoPlayer } from "@/components/MatchVideoPlayer";

export type FinishedMatchWithResultado = FinishedMatch & { resultado: string | null };

const POINT_TYPE_TAG: Record<ScoredPoint["tipo"], { label: string; className: string }> = {
  winner: { label: "W", className: "bg-brand-50 text-brand-700" },
  error_forzado: { label: "EF", className: "bg-amber-50 text-amber-700" },
  error_no_forzado: { label: "ENF", className: "bg-red-50 text-red-600" },
};

// Mismos dos colores "de categoría" (independientes del verde/morado/naranja de marca por rol)
// que ya usa el resto del panel para distinguir cosas sin relación con el rol del usuario — aquí
// para que se vea de un vistazo quién va en qué equipo.
const TEAM_COLOR: Record<"A" | "B", string> = {
  A: "var(--color-chart-blue)",
  B: "var(--color-chart-orange)",
};

function formatOffset(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Posiciones 1-2 = Equipo A, 3-4 = Equipo B — misma convención que ya usa ScoreboardOverlay
// (saque > 2 → equipo B) para saber quién saca.
function teamOfPosition(position: number): "A" | "B" {
  return position > 2 ? "B" : "A";
}

// Un "juego" dentro de un set desplegado: también es su propio desplegable (un set entero puede
// tener 10+ juegos en un partido completo, así que no conviene enseñarlos todos abiertos a la
// vez) — cabecera con su número, quién lo ganó y cuántos puntos duró, y al abrirlo la lista de
// puntos con el marcador real (15/30/40...) ya calculado.
function GameGroup({
  points,
  activeClipUrl,
  onSelectClip,
  expanded,
  onToggle,
}: {
  points: ScoredPoint[];
  activeClipUrl: string | null;
  onSelectClip: (url: string | null) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const winnerPoint = points.find((p) => p.gameWon);
  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 rounded-md px-1 py-1 text-left text-[10px] font-semibold tracking-wide text-ink-400 uppercase transition-colors hover:bg-ink-50"
      >
        <ChevronRight className={`h-3 w-3 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
        Juego {points[0]?.gameNumber}
        {points[0]?.isTiebreak && " · Tie-break"}
        {winnerPoint && (
          <span className="font-medium text-ink-300 normal-case">· Equipo {winnerPoint.gameWinner} ({points.length} pts)</span>
        )}
      </button>
      {expanded && (
        <ul className="flex flex-col gap-1 pl-1">
          {points.map((p) => {
            const tag = POINT_TYPE_TAG[p.tipo];
            return (
              <li key={p.id}>
                <button
                  onClick={() => onSelectClip(p.clipUrl)}
                  className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-brand-50 ${
                    activeClipUrl === p.clipUrl ? "bg-brand-50 text-brand-700" : "text-ink-600"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="shrink-0 rounded-full"
                      style={{ width: 6, height: 6, background: TEAM_COLOR[p.equipo] }}
                      title={`Equipo ${p.equipo}`}
                    />
                    <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${tag.className}`}>{tag.label}</span>
                    <span className="shrink-0 font-semibold">{p.gameWon ? "Juego" : p.scoreLabel}</span>
                    {p.goldenPoint && <span className="shrink-0 text-amber-600">(punto de oro)</span>}
                    {p.gameWon && <span className="shrink-0 font-medium text-brand-600">· Equipo {p.gameWinner}</span>}
                    {p.serverName && <span className="truncate text-ink-400">· Saca {p.serverName}</span>}
                  </span>
                  <span className="shrink-0 text-ink-400">{formatOffset(p.offsetSeconds)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SetAccordion({
  setJuego,
  points,
  clipUrl,
  clipUrlActual,
  setClipUrl,
  expanded,
  onToggle,
}: {
  setJuego: number;
  points: ScoredPoint[];
  clipUrl: string | null;
  clipUrlActual: string | null;
  setClipUrl: (url: string | null) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const games = useMemo(() => {
    const byGame = new Map<number, ScoredPoint[]>();
    for (const p of points) {
      if (!byGame.has(p.gameNumber)) byGame.set(p.gameNumber, []);
      byGame.get(p.gameNumber)!.push(p);
    }
    return [...byGame.entries()].sort((x, y) => x[0] - y[0]);
  }, [points]);

  // El primer juego del set empieza abierto (igual que el primer set de todos); el resto, a
  // mano — un set completo puede tener 10+ juegos y no caben bien todos abiertos de golpe.
  const [expandedGames, setExpandedGames] = useState<Set<number>>(() => (games.length ? new Set([games[0][0]]) : new Set()));
  function toggleGame(gameNumber: number) {
    setExpandedGames((prev) => {
      const next = new Set(prev);
      if (next.has(gameNumber)) next.delete(gameNumber);
      else next.add(gameNumber);
      return next;
    });
  }

  const { gamesA, gamesB } = useMemo(() => {
    let a = 0;
    let b = 0;
    for (const p of points) {
      if (p.gameWon && p.gameWinner === "A") a++;
      else if (p.gameWon && p.gameWinner === "B") b++;
    }
    return { gamesA: a, gamesB: b };
  }, [points]);

  return (
    <div className="rounded-lg border border-ink-100">
      <div className="flex items-center justify-between gap-2 rounded-t-lg bg-ink-50 pr-2">
        <button
          onClick={onToggle}
          className="flex flex-1 items-center gap-1.5 px-3 py-2 text-left text-sm font-semibold text-ink-700"
        >
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`} />
          Set {setJuego}
          <span className="font-normal text-ink-400">
            {gamesA}-{gamesB}
          </span>
        </button>
        {clipUrl && (
          <button
            onClick={() => setClipUrl(clipUrl)}
            title="Ver set completo"
            className="shrink-0 rounded-md p-1.5 text-ink-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
          >
            <Play className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {expanded && (
        <div className="flex flex-col gap-2 border-t border-ink-100 px-2 py-2">
          {games.map(([gameNumber, gamePoints]) => (
            <GameGroup
              key={gameNumber}
              points={gamePoints}
              activeClipUrl={clipUrlActual}
              onSelectClip={setClipUrl}
              expanded={expandedGames.has(gameNumber)}
              onToggle={() => toggleGame(gameNumber)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Todo el contenido de la ficha de un partido jugado (marcador, equipos, estadísticas, vídeo y
// sets desplegables) — independiente de si se muestra dentro de un Modal (escritorio) o de una
// página entera (móvil, ver MatchDetailPage), que es justo lo único que cambia entre los dos.
export function MatchTimelineContent({ matchId }: { matchId: number }) {
  const [clipUrl, setClipUrl] = useState<string | null>(null);
  const [expandedSets, setExpandedSets] = useState<Set<number>>(new Set());

  const { data: timeline, isLoading } = useQuery({
    queryKey: ["matches", matchId, "timeline"],
    queryFn: async () => (await api.get<MatchTimeline>(`/matches/${matchId}/timeline`)).data,
  });

  const activeClip = clipUrl ?? timeline?.fullClipUrl;

  const scoredPoints = useMemo(() => (timeline ? scorePoints(timeline.points) : []), [timeline]);
  const pointsBySet = useMemo(() => {
    const map = new Map<number, ScoredPoint[]>();
    for (const p of scoredPoints) {
      if (!map.has(p.setJuego)) map.set(p.setJuego, []);
      map.get(p.setJuego)!.push(p);
    }
    return map;
  }, [scoredPoints]);

  // Quién va con quién (posición 1-2 / 3-4) y quién ganó, para que arriba quede claro de un
  // vistazo — no solo una tabla de jugadores sueltos.
  const teams = useMemo(() => {
    if (!timeline) return [];
    const byTeam: Record<"A" | "B", string[]> = { A: [], B: [] };
    for (const p of timeline.playerStats) {
      byTeam[teamOfPosition(p.position)].push(p.name ?? `Jugador ${p.position}`);
    }
    const winner =
      timeline.finalScore && timeline.finalScore.setsA !== timeline.finalScore.setsB
        ? timeline.finalScore.setsA > timeline.finalScore.setsB
          ? "A"
          : "B"
        : null;
    return (["A", "B"] as const)
      .filter((team) => byTeam[team].length)
      .map((team) => ({ team, names: byTeam[team], isWinner: winner === team }));
  }, [timeline]);

  // El primer set empieza desplegado para que al abrir la ficha ya se vea algo; el resto se
  // despliega a mano. Se fija una vez que llega la timeline (no en el estado inicial, que
  // todavía no la conoce).
  useEffect(() => {
    if (timeline?.sets.length) {
      setExpandedSets((prev) => (prev.size === 0 ? new Set([timeline.sets[0].setJuego]) : prev));
    }
  }, [timeline]);

  function toggleSet(setJuego: number) {
    setExpandedSets((prev) => {
      const next = new Set(prev);
      if (next.has(setJuego)) next.delete(setJuego);
      else next.add(setJuego);
      return next;
    });
  }

  if (isLoading) return <p className="text-sm text-ink-400">Cargando repetición…</p>;
  if (!timeline) return <p className="text-sm text-ink-400">No se pudo cargar la repetición de este partido.</p>;

  return (
    <div className="flex flex-col gap-4">
      {!!teams.length && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {teams.map(({ team, names, isWinner }) => (
            <span
              key={team}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
                isWinner ? "border-amber-300 bg-amber-50 text-amber-800" : "border-ink-100 bg-white text-ink-600"
              }`}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: TEAM_COLOR[team] }} />
              {names.join(" · ")}
              {isWinner && <Trophy className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
            </span>
          ))}
        </div>
      )}

      {timeline.finalScore && (
        <div className="flex flex-wrap items-center justify-center gap-6 rounded-lg bg-ink-50 py-3 text-sm font-semibold text-ink-700">
          <span>
            Sets {timeline.finalScore.setsA} – {timeline.finalScore.setsB}
          </span>
          <span className="text-ink-300">·</span>
          <span>
            Último set {timeline.finalScore.gamesA} – {timeline.finalScore.gamesB}
          </span>
          {timeline.mvp?.name && (
            <>
              <span className="text-ink-300">·</span>
              <span className="inline-flex items-center gap-1.5 text-amber-600">
                <Trophy className="h-4 w-4" />
                MVP: {timeline.mvp.name} ({timeline.mvp.winners})
              </span>
            </>
          )}
        </div>
      )}

      {!!timeline.playerStats.length && (
        <div className="overflow-x-auto rounded-lg border border-ink-100">
          <table className="w-full min-w-[420px] text-left text-xs">
            <thead className="bg-ink-50/60 text-[10px] tracking-wide text-ink-400 uppercase">
              <tr>
                <th className="px-3 py-2">Jugador</th>
                <th className="px-3 py-2">Winners</th>
                <th className="px-3 py-2">Error forzado</th>
                <th className="px-3 py-2">Error no forzado</th>
              </tr>
            </thead>
            <tbody>
              {timeline.playerStats.map((p) => (
                <tr key={p.position} className="border-t border-ink-100" style={{ borderLeft: `3px solid ${TEAM_COLOR[teamOfPosition(p.position)]}` }}>
                  <td className="px-3 py-2 font-medium text-ink-700">{p.name ?? `Jugador ${p.position}`}</td>
                  <td className="px-3 py-2 text-ink-500">{p.winners}</td>
                  <td className="px-3 py-2 text-ink-500">{p.errorForzado}</td>
                  <td className="px-3 py-2 text-ink-500">{p.errorNoForzado}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="sm:w-3/5">
          {timeline.fullClipUrl ? (
            <>
              {activeClip && <MatchVideoPlayer src={activeClip} className="aspect-video w-full rounded-lg" />}
              <Button className="mt-2 w-full" onClick={() => setClipUrl(timeline.fullClipUrl)}>
                <Play className="h-4 w-4" />
                Ver partido completo
              </Button>
            </>
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-ink-50 text-sm text-ink-400">
              Sin vídeo disponible para este partido
            </div>
          )}
        </div>

        <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto sm:w-2/5">
          {timeline.sets.map((set) => (
            <SetAccordion
              key={set.setJuego}
              setJuego={set.setJuego}
              points={pointsBySet.get(set.setJuego) ?? []}
              clipUrl={set.clipUrl}
              clipUrlActual={clipUrl}
              setClipUrl={setClipUrl}
              expanded={expandedSets.has(set.setJuego)}
              onToggle={() => toggleSet(set.setJuego)}
            />
          ))}
          {!timeline.sets.length && <p className="text-sm text-ink-400">No hay puntos registrados para este partido.</p>}
        </div>
      </div>
    </div>
  );
}

export function MatchDetailModal({ match, onOpenChange }: { match: FinishedMatchWithResultado; onOpenChange: (open: boolean) => void }) {
  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title={match.court?.name ?? match.courtLabel}
      description={`${match.club.nombre} · ${match.customerName}`}
      size="lg"
    >
      <MatchTimelineContent matchId={match.id} />
    </Modal>
  );
}
